import {format, parse} from 'date-fns';

/**
 * Removes the timezone offset from an ISO-8601 timestamp.
 * @param {string?} timestamp An ISO-8601 timestamp
 * @returns {string?} The timestamp without the timezone offset
 * - `2021-01-01T00:00:00+01:00 => 2021-01-01T00:00:00`
 * - `2021-01-01T00:00:00Z => 2021-01-01T00:00:00`
 */
export const removeTimeOffset = (timestamp) => {
    if (!timestamp) return timestamp;
    if (timestamp.endsWith('Z')) return timestamp.replace('Z', '');
    if (timestamp.includes('+')) return timestamp.split('+')[0];
    return timestamp;
};

/**
 * Formats an ISO-8601 timestamp to a human-readable format.
 * Extracts the date part from the timestamp, ignores the time part from the timestamp
 * and instead substitutes the time part with midnight at the browser's timezone.
 *
 * @param {string} timestamp
 * An ISO-8601 timestamp.
 *
 * @param {string} fmt
 * The format to use, as specified by date-fns.
 *
 * @param  {...any} modifiers
 * Optional date-fns modifiers to be applied on the parsed date before formatting.
 */
export const formatDate = (timestamp, fmt, ...modifiers) => {
    const datePart = timestamp
        // remove the 'T' separator
        // 2023-03-24T14:30:00.0000000 => 2023-03-24 14:30:00.0000000
        .replace('T', ' ')
        // remove fraction of second
        // 2023-03-24 14:30:00.0000000 => 2023-03-24 14:30:00
        .split('.')[0];
    const date = parse(datePart, 'yyyy-MM-dd HH:mm:ss', new Date());
    const result = modifiers.reduce((acc, modifier) => modifier(acc), date);
    return format(result, fmt);
};

/**
 * Use this function to serialize a `Date` as an ISO-8601 timestamp.
 * Ignore the locale of the browser. Does not perform any conversion for the
 * date and time parts, uses them as-is. Applies the modifiers, if any.
 * Takes into account Daylight Saving Time (DST) and includes the offset in the timestamp;
 * See examples below.
 *
 * @param {Date} source
 * The source date to extract the date part from.
 *
 * @param {...((Date) => Date)} modifiers
 * Optional date-fns modifiers to be applied on the parsed date before formatting,
 * for example `startOfDay` or `endOfDay`.
 *
 * @returns {string}
 * An ISO-8601 timestamp. Examples:
 * - `new Date(2023, 0, 1, 14) => 2023-01-01T14:00:00+01:00`
 * - `new Date(2023, 6, 1, 9) => 2023-07-01T09:00:00+02:00` (DST)
 */
export const toDutchISOTimestamp = (source, ...modifiers) => {
    const date = modifiers.reduce((acc, modifier) => modifier(acc), source);

    // The offset is either +01:00 (Standard time) or +02:00 (DST)
    const offset = isDST(date) ? 2 : 1;
    const offsetPart = `+0${offset}:00`;

    // The date part is always in the format yyyy-MM-dd
    const datePart = [date.getFullYear(), date.getMonth() + 1, date.getDate()]
        .map((n) => n.toString().padStart(2, '0'))
        .join('-');

    // The time part is always in the format HH:MM:SS
    const timePart = [date.getHours(), date.getMinutes(), date.getSeconds()]
        .map((n) => n.toString().padStart(2, '0'))
        .join(':');

    return `${datePart}T${timePart}${offsetPart}`;
};

/**
 * Checks if a date is in Daylight Saving Time (DST)
 * @param {Date} date The date to check for DST
 * @returns `true` if the date is in DST, `false` otherwise
 */
export const isDST = (date) => {
    /*
     * No country observes DST that lasts for seven months.
     * https://www.timeanddate.com/time/dst/2023.html
     * In an area that observes DST the offset from UTC time
     * in January will be different to the one in July.
     *
     * While Daylight Savings Time moves clocks forwards,
     * JavaScript always returns a greater value during Standard Time.
     * Therefore, getting the minimum offset between January and July
     * will get the timezone offset during DST.
     *
     * We then check if the dates timezone is equal to that minimum value.
     * If it is, then we are in DST; otherwise we are not.
     */
    const jan = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
    const jul = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
    return Math.max(jan, jul) !== date.getTimezoneOffset();
};

window.datetime = {toDutchISOTimestamp, isDST, formatDate, parse};
